import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import {
  Search,
  Users,
  X,
  Check,
  Upload,
  User,
  Lock,
  Trash2,
  Folder,
  FolderPlus,
  ChevronDown,
  Briefcase,
  Calendar,
  FileText,
  Loader2,
  Sparkles
} from 'lucide-react';
import FileIcon from './FileIcon';
import api from '../../utils/api';

const FileUploadModal = ({
  phase: initialPhase = 'onboarding',
  folderId: initialFolderId = null,
  onClose,
  onSuccess,
  showLifecycleSelector = true,
  files: existingFiles = [] // Rename prop to avoid conflict with local state
}) => {
  // File state
  const [files, setFiles] = useState([]);
  const [documentName, setDocumentName] = useState('');
  const [dragActive, setDragActive] = useState(false);

  // Folder state
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(initialFolderId);
  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolderLoading, setCreatingFolderLoading] = useState(false);

  // Audience state (Who is this for?)
  const [audience, setAudience] = useState('company-wide'); // 'company-wide', 'role-specific', 'employee-specific', 'private'
  const [roleSpecific, setRoleSpecific] = useState('');
  const [employeeSpecificIds, setEmployeeSpecificIds] = useState([]);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');

  // Lifecycle state
  const [lifecycleStage, setLifecycleStage] = useState(initialPhase || 'onboarding');

  // Additional fields
  const [reviewByDate, setReviewByDate] = useState('');
  const [description, setDescription] = useState('');
  const [requiresAcknowledgment, setRequiresAcknowledgment] = useState(false);

  // Data
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);

  // UI state
  const [uploading, setUploading] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateFile, setDuplicateFile] = useState(null);

  // AI Classification state
  const [isClassifying, setIsClassifying] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);

  // Refs
  const folderDropdownRef = useRef(null);
  const newFolderInputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchRoles();
    fetchEmployees();
    fetchFolders();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (folderDropdownRef.current && !folderDropdownRef.current.contains(event.target)) {
        setShowFolderDropdown(false);
        setIsCreatingFolder(false);
        setNewFolderName('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus new folder input when creating
  useEffect(() => {
    if (isCreatingFolder && newFolderInputRef.current) {
      newFolderInputRef.current.focus();
    }
  }, [isCreatingFolder]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      if (response.data.employees) {
        setEmployees(response.data.employees);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get('/employees/roles');
      if (response.data.roles) {
        setRoles(response.data.roles);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchFolders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/folders', {
        headers: { Authorization: `Bearer ${token}` },
        params: { parent_id: 'null' } // Get root folders
      });
      if (response.data.success) {
        setFolders(response.data.folders);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  // File handling
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = async (fileList) => {
    const newFiles = Array.from(fileList);
    setFiles(newFiles);

    // Auto-populate document name from first file if empty
    if (!documentName && newFiles.length > 0) {
      // Remove extension from filename
      const fileName = newFiles[0].name;
      const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
      setDocumentName(nameWithoutExt);

      // Trigger AI Classification if no folder is selected
      if (!selectedFolderId && newFiles.length === 1) {
        classifyFile(newFiles[0]);
      }
    }
  };

  const classifyFile = async (file) => {
    try {
      setIsClassifying(true);
      setAiSuggestion(null);

      const formData = new FormData();
      formData.append('files', file);

      const token = localStorage.getItem('token');
      const response = await api.post('/files/ai/classify', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success && response.data.suggestion) {
        setAiSuggestion({
          folder: response.data.suggestion,
          confidence: response.data.confidence,
          reasoning: response.data.reasoning
        });

        // Auto-select if high confidence (> 0.7)
        if (response.data.confidence > 0.7) {
          setSelectedFolderId(response.data.suggestion.id);
          toast.success(`AI suggested folder: ${response.data.suggestion.name}`, {
            icon: '✨',
            duration: 4000
          });
        }
      }
    } catch (error) {
      console.error('Error classifying file:', error);
      // Fail silently, don't block user
    } finally {
      setIsClassifying(false);
    }
  };

  const removeFile = () => {
    setFiles([]);
    setDocumentName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Folder handling
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      setCreatingFolderLoading(true);
      const token = localStorage.getItem('token');
      const response = await api.post('/folders', {
        name: newFolderName.trim(),
        parent_id: null,
        phase: lifecycleStage,
        color: '#6366f1'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const newFolder = response.data.folder;
        setFolders(prev => [...prev, newFolder]);
        setSelectedFolderId(newFolder.id);
        setIsCreatingFolder(false);
        setNewFolderName('');
        setShowFolderDropdown(false);
        toast.success('Folder created');
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error('Failed to create folder');
    } finally {
      setCreatingFolderLoading(false);
    }
  };

  const handleFolderKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateFolder();
    } else if (e.key === 'Escape') {
      setIsCreatingFolder(false);
      setNewFolderName('');
    }
  };

  // Employee handling
  const handleEmployeeToggle = (employeeId) => {
    if (employeeSpecificIds.includes(employeeId)) {
      setEmployeeSpecificIds(employeeSpecificIds.filter((id) => id !== employeeId));
    } else {
      setEmployeeSpecificIds([...employeeSpecificIds, employeeId]);
    }
  };

  // Filter employees by lifecycle stage and search
  const filteredEmployees = employees.filter(emp => {
    // Filter by lifecycle
    if (lifecycleStage === 'onboarding') {
      if (emp.status !== 'onboarding' && emp.status !== 'active') return false;
    } else if (lifecycleStage === 'preboarding') {
      if (emp.status !== 'preboarding') return false;
    }
    // For 'both', show all employees

    // Filter by search
    if (employeeSearchQuery) {
      const searchLower = employeeSearchQuery.toLowerCase();
      const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
      const email = (emp.email || '').toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower);
    }
    return true;
  });

  const selectedEmployees = employees.filter(emp => employeeSpecificIds.includes(emp.id));
  const selectedFolder = folders.find(f => f.id === selectedFolderId);

  // Validation
  const isValid = files.length > 0 && documentName.trim() !== '';

  // Upload handler
  const handleUpload = async (forceNew = false) => {
    if (!isValid) return;

    // Duplicate detection (only if not forcing new upload)
    if (!forceNew && !showDuplicateModal) {
      let checkName = documentName.trim();

      // If we have a file, ensure we're checking against the name + extension
      if (files.length > 0) {
        const file = files[0];
        const ext = file.name.split('.').pop();
        if (ext && !checkName.toLowerCase().endsWith('.' + ext.toLowerCase())) {
          checkName += '.' + ext;
        }
      }

      const duplicate = existingFiles.find(f =>
        f.file_name.toLowerCase() === checkName.toLowerCase() &&
        (f.folder_id === selectedFolderId || (f.folder_id === null && selectedFolderId === null))
      );

      if (duplicate) {
        setDuplicateFile(duplicate);
        setShowDuplicateModal(true);
        return;
      }
    }

    if (audience === 'role-specific' && !roleSpecific) {
      toast.error('Please select a role');
      return;
    }

    if (audience === 'employee-specific' && employeeSpecificIds.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();

      files.forEach((file) => {
        formData.append('files', file);
      });

      // Map audience to file_category
      formData.append('file_category', audience);
      formData.append('phase', lifecycleStage);
      formData.append('custom_name', documentName.trim());

      if (selectedFolderId) {
        formData.append('folder_id', selectedFolderId);
      }

      if (audience === 'role-specific') {
        formData.append('role_specific', roleSpecific);
      }

      if (audience === 'employee-specific') {
        formData.append('employee_specific_ids', JSON.stringify(employeeSpecificIds));
      }

      if (reviewByDate) {
        formData.append('review_by_date', reviewByDate);
      }

      if (description.trim()) {
        formData.append('description', description.trim());
      }

      formData.append('requires_acknowledgment', requiresAcknowledgment);
      formData.append('release_status', 'published');

      await api.post('/files/company', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      onSuccess();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleReplace = async () => {
    if (!duplicateFile) return;

    try {
      setUploading(true);
      const formData = new FormData();

      const fileToUpload = files[0];
      formData.append('files', fileToUpload);

      // Valid fields for replace endpoint
      if (reviewByDate) formData.append('review_by_date', reviewByDate);
      if (description.trim()) formData.append('description', description.trim());
      formData.append('requires_acknowledgment', requiresAcknowledgment);
      formData.append('release_status', 'published');

      await api.post(`/files/company/${duplicateFile.id}/replace`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('File replaced successfully');
      // Update UI
      setShowDuplicateModal(false);
      setDuplicateFile(null);
      onSuccess();
    } catch (error) {
      console.error('Replace error:', error);
      toast.error(error.response?.data?.message || 'Failed to replace document');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      {/* Duplicate Resolution Modal */}
      {showDuplicateModal && (
        <div className="absolute inset-0 z-10 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4 mx-auto">
              <FileText size={24} className="text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 text-center mb-2">Duplicate Document</h3>
            <p className="text-gray-600 text-center mb-6">
              A document named <span className="font-semibold text-gray-900">"{duplicateFile?.file_name}"</span> already exists in this folder.
            </p>

            <div className="space-y-3">
              <button
                onClick={handleReplace}
                disabled={uploading}
                className="w-full py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary-dark transition-colors flex items-center justify-center gap-2"
              >
                {uploading ? <Loader2 size={18} className="animate-spin" /> : null}
                Replace Existing Version
              </button>
              <button
                onClick={() => handleUpload(true)}
                disabled={uploading}
                className="w-full py-3 bg-white border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
              >
                Keep Both (Upload as New)
              </button>
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicateFile(null);
                }}
                disabled={uploading}
                className="w-full py-3 text-gray-500 hover:text-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl relative">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Upload Document</h2>
            <p className="text-sm text-gray-500 mt-0.5">Add a new document to your library</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* 1. File Upload Area */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Document File <span className="text-red-500">*</span>
            </label>

            {files.length === 0 ? (
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${dragActive
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Upload size={24} className="text-primary" />
                </div>
                <p className="text-sm font-medium text-gray-700 mb-1">
                  Drag and drop your document here, or click to browse
                </p>
                <p className="text-xs text-gray-500">
                  PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, Images up to 50MB
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileInput}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif"
                />
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-gray-200">
                  <FileIcon fileType={files[0].type} size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{files[0].name}</p>
                  <p className="text-xs text-gray-500">{formatFileSize(files[0].size)}</p>
                </div>
                <button
                  onClick={removeFile}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            )}
          </div>

          {/* 2. Document Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Document Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              placeholder="Enter document name..."
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none text-sm"
            />
          </div>

          {/* 3. Folder Selection */}
          <div>
            {/* AI Suggestion */}
            {isClassifying && (
              <div className="mb-3 p-3 bg-purple-50 border border-purple-100 rounded-xl flex items-center gap-3 animate-pulse">
                <Sparkles size={18} className="text-purple-600" />
                <p className="text-sm text-purple-700 font-medium">Analyzing document to suggest a folder...</p>
              </div>
            )}

            {aiSuggestion && !isClassifying && selectedFolderId === aiSuggestion.folder?.id && (
              <div className="mb-3 p-3 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 rounded-xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                <Sparkles size={18} className="text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-purple-900 font-medium">
                    AI Suggested: <span className="font-bold">{aiSuggestion.folder.name}</span>
                  </p>
                  <p className="text-xs text-purple-700 mt-0.5">
                    {aiSuggestion.reasoning}
                  </p>
                </div>
              </div>
            )}

            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Folder
            </label>
            <div className="relative" ref={folderDropdownRef}>
              <button
                type="button"
                onClick={() => setShowFolderDropdown(!showFolderDropdown)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-left flex items-center justify-between hover:border-gray-400 transition-colors text-sm"
              >
                <span className="flex items-center gap-2">
                  <Folder size={16} className="text-gray-400" />
                  <span className={selectedFolder ? 'text-gray-900' : 'text-gray-500'}>
                    {selectedFolder ? selectedFolder.name : 'No folder (root level)'}
                  </span>
                </span>
                <ChevronDown size={16} className={`text-gray-400 transition-transform ${showFolderDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showFolderDropdown && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                  <div className="max-h-48 overflow-y-auto">
                    {/* No folder option */}
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFolderId(null);
                        setShowFolderDropdown(false);
                      }}
                      className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors ${!selectedFolderId ? 'bg-primary/5 text-primary' : 'text-gray-700'
                        }`}
                    >
                      <Folder size={16} className={!selectedFolderId ? 'text-primary' : 'text-gray-400'} />
                      No folder (root level)
                      {!selectedFolderId && <Check size={16} className="ml-auto" />}
                    </button>

                    {/* Existing folders */}
                    {folders.map((folder) => (
                      <button
                        key={folder.id}
                        type="button"
                        onClick={() => {
                          setSelectedFolderId(folder.id);
                          setShowFolderDropdown(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors ${selectedFolderId === folder.id ? 'bg-primary/5 text-primary' : 'text-gray-700'
                          }`}
                      >
                        <Folder size={16} style={{ color: folder.color || '#6366f1' }} />
                        {folder.name}
                        {selectedFolderId === folder.id && <Check size={16} className="ml-auto" />}
                      </button>
                    ))}
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-200"></div>

                  {/* Create new folder */}
                  {isCreatingFolder ? (
                    <div className="p-3">
                      <div className="flex items-center gap-2">
                        <FolderPlus size={16} className="text-primary flex-shrink-0" />
                        <input
                          ref={newFolderInputRef}
                          type="text"
                          value={newFolderName}
                          onChange={(e) => setNewFolderName(e.target.value)}
                          onKeyDown={handleFolderKeyPress}
                          placeholder="Folder name..."
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                          disabled={creatingFolderLoading}
                        />
                        <button
                          type="button"
                          onClick={handleCreateFolder}
                          disabled={!newFolderName.trim() || creatingFolderLoading}
                          className="p-1.5 text-primary hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {creatingFolderLoading ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Check size={16} />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsCreatingFolder(false);
                            setNewFolderName('');
                          }}
                          className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1.5 ml-6">Press Enter to create</p>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsCreatingFolder(true)}
                      className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 text-primary hover:bg-primary/5 transition-colors"
                    >
                      <FolderPlus size={16} />
                      Create New Folder
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 4. Who is this for? (Audience) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Who is this for?
            </label>
            <div className="space-y-2">
              {[
                { value: 'company-wide', label: 'Everyone', icon: Users, description: 'All employees at relevant lifecycle stage(s)' },
                { value: 'role-specific', label: 'A specific role', icon: Briefcase, description: 'Only employees with a specific role' },
                { value: 'employee-specific', label: 'A specific person', icon: User, description: 'Only selected employees' },
                { value: 'private', label: 'Admin only', icon: Lock, description: 'Only admins can see this document' },
              ].map(({ value, label, icon: Icon, description }) => (
                <label
                  key={value}
                  className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${audience === value
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                    }`}
                >
                  <input
                    type="radio"
                    name="audience"
                    value={value}
                    checked={audience === value}
                    onChange={() => {
                      setAudience(value);
                      setRoleSpecific('');
                      setEmployeeSpecificIds([]);
                    }}
                    className="sr-only"
                  />
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${audience === value ? 'bg-primary/10' : 'bg-gray-100'
                    }`}>
                    <Icon size={18} className={audience === value ? 'text-primary' : 'text-gray-500'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${audience === value ? 'text-primary' : 'text-gray-900'}`}>
                      {label}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${audience === value ? 'border-primary bg-primary' : 'border-gray-300'
                    }`}>
                    {audience === value && <Check size={12} className="text-white" />}
                  </div>
                </label>
              ))}
            </div>

            {/* Role Selection (if role-specific) */}
            {audience === 'role-specific' && (
              <div className="mt-4 ml-13 pl-13">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Role <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-left flex items-center justify-between hover:border-gray-400 transition-colors text-sm"
                  >
                    <span className={roleSpecific ? 'text-gray-900' : 'text-gray-500'}>
                      {roleSpecific || 'Select a role...'}
                    </span>
                    <ChevronDown size={16} className={`text-gray-400 transition-transform ${showRoleDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {showRoleDropdown && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                      <div className="max-h-48 overflow-y-auto">
                        {roles.length === 0 ? (
                          <p className="px-4 py-3 text-sm text-gray-500">No roles found</p>
                        ) : (
                          roles.map((role) => (
                            <button
                              key={role.id || role.role_name}
                              type="button"
                              onClick={() => {
                                setRoleSpecific(role.role_name);
                                setShowRoleDropdown(false);
                              }}
                              className={`w-full px-4 py-2.5 text-left text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${roleSpecific === role.role_name ? 'bg-primary/5 text-primary' : 'text-gray-700'
                                }`}
                            >
                              {role.role_name}
                              {roleSpecific === role.role_name && <Check size={16} />}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Employee Selection (if employee-specific) */}
            {audience === 'employee-specific' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Employees <span className="text-red-500">*</span>
                </label>

                {/* Selected employees chips */}
                {selectedEmployees.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedEmployees.map(emp => (
                      <span
                        key={emp.id}
                        className="inline-flex items-center gap-1 bg-primary/10 text-primary px-2.5 py-1 rounded-full text-xs font-medium"
                      >
                        {emp.first_name} {emp.last_name}
                        <button
                          type="button"
                          onClick={() => handleEmployeeToggle(emp.id)}
                          className="hover:bg-primary/20 rounded-full p-0.5"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowEmployeeDropdown(!showEmployeeDropdown)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-left flex items-center justify-between hover:border-gray-400 transition-colors text-sm"
                  >
                    <span className="text-gray-500">
                      {employeeSpecificIds.length === 0
                        ? 'Search and select employees...'
                        : `${employeeSpecificIds.length} employee(s) selected`}
                    </span>
                    <ChevronDown size={16} className={`text-gray-400 transition-transform ${showEmployeeDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {showEmployeeDropdown && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                      {/* Search */}
                      <div className="p-3 border-b border-gray-100">
                        <div className="relative">
                          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            value={employeeSearchQuery}
                            onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                            placeholder="Search employees..."
                            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                          />
                        </div>
                      </div>

                      {/* Employee list */}
                      <div className="max-h-48 overflow-y-auto">
                        {filteredEmployees.length === 0 ? (
                          <p className="px-4 py-3 text-sm text-gray-500 text-center">No employees found</p>
                        ) : (
                          filteredEmployees.map(emp => (
                            <label
                              key={emp.id}
                              className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={employeeSpecificIds.includes(emp.id)}
                                onChange={() => handleEmployeeToggle(emp.id)}
                                className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {emp.first_name} {emp.last_name}
                                </p>
                                <p className="text-xs text-gray-500 truncate">{emp.email}</p>
                              </div>
                            </label>
                          ))
                        )}
                      </div>

                      {/* Footer */}
                      <div className="p-2 border-t border-gray-100 flex justify-end">
                        <button
                          type="button"
                          onClick={() => setShowEmployeeDropdown(false)}
                          className="px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 5. Lifecycle Stage */}
          {showLifecycleSelector && audience !== 'private' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Applies to which employees?
              </label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'preboarding', label: 'Preboarding', color: 'amber', description: 'Only preboarding employees see this' },
                  { value: 'onboarding', label: 'Onboarding', color: 'blue', description: 'Only onboarding employees see this' },
                  { value: 'both', label: 'Both', color: 'purple', description: 'Visible to both preboarding and onboarding' },
                ].map(({ value, label, color }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setLifecycleStage(value)}
                    className={`p-3 border-2 rounded-xl text-center transition-all ${lifecycleStage === value
                      ? ''
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    style={lifecycleStage === value ? {
                      borderColor: color === 'amber' ? '#f59e0b' : color === 'blue' ? '#3b82f6' : '#8b5cf6',
                      backgroundColor: color === 'amber' ? '#fffbeb' : color === 'blue' ? '#eff6ff' : '#f5f3ff',
                      color: color === 'amber' ? '#b45309' : color === 'blue' ? '#1d4ed8' : '#6d28d9'
                    } : {}}
                  >
                    <span className="font-medium text-sm">{label}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {lifecycleStage === 'preboarding' && 'Document will only be visible to preboarding employees'}
                {lifecycleStage === 'onboarding' && 'Document will only be visible to onboarding employees'}
                {lifecycleStage === 'both' && 'Document will be visible to both preboarding and onboarding employees'}
              </p>
            </div>
          )}

          {/* 6. Review By Date (Optional) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Review By Date
              <span className="font-normal text-gray-500 ml-1">(optional)</span>
            </label>
            <div className="relative">
              <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="date"
                value={reviewByDate}
                onChange={(e) => setReviewByDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none text-sm"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              Set a date if this document needs periodic review or renewal
            </p>
          </div>

          {/* 7. Description (Optional) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description
              <span className="font-normal text-gray-500 ml-1">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add any notes about this document..."
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none text-sm resize-none"
            />
          </div>

          {/* Require Acknowledgment */}
          {audience !== 'private' && (
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={requiresAcknowledgment}
                  onChange={(e) => setRequiresAcknowledgment(e.target.checked)}
                  className="w-5 h-5 text-primary rounded border-gray-300 focus:ring-primary"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Require Employee Acknowledgment</p>
                  <p className="text-xs text-gray-500">Employees must mark this document as "Reviewed"</p>
                </div>
              </label>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={uploading}
            className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition-colors font-medium text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => handleUpload(false)}
            disabled={uploading || !isValid}
            className="px-5 py-2.5 bg-gradient-to-r from-primary to-indigo-600 text-white rounded-xl hover:shadow-lg hover:shadow-primary/25 transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {uploading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={16} />
                Upload
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileUploadModal;
